import React, { useState } from 'react'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { getClassByNumber } from '../utils/classSchedule'
import FileUpload from './FileUpload'

const { FiX, FiPlus, FiSave } = FiIcons

const AddClassModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    classNumber: '',
    zoomLink: '',
    zoomLink2: '',
    notes: '',
    audioLink: '',
    images: []
  })
  const [newImageUrl, setNewImageUrl] = useState('')
  const [loading, setLoading] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const classNumber = parseInt(formData.classNumber)
      if (isNaN(classNumber) || classNumber < 1 || classNumber > 285) {
        alert('Please enter a valid class number between 1 and 285')
        setLoading(false)
        return
      }

      const scheduleInfo = getClassByNumber(classNumber)
      if (!scheduleInfo) {
        alert('Invalid class number')
        setLoading(false)
        return
      }

      // Filter out empty URLs and validate
      const validImages = Array.isArray(formData.images) ? 
        formData.images.filter(img => img && img.trim() && validateImageUrl(img.trim())) : []

      const classData = {
        class_number: classNumber,
        class_date: scheduleInfo.date.toISOString(),
        zoom_link: formData.zoomLink.trim() || null,
        zoom_link_2: formData.zoomLink2.trim() || null,
        notes: formData.notes.trim() || null,
        audio_link: formData.audioLink.trim() || null,
        images: validImages
      }

      const result = await onAdd(classData)
      
      if (result?.success) {
        // Reset form
        setFormData({
          classNumber: '',
          zoomLink: '',
          zoomLink2: '',
          notes: '',
          audioLink: '',
          images: []
        })
        setNewImageUrl('')
        onClose()
      } else {
        alert('Error adding class: ' + (result?.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error)
      alert('Error adding class: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const validateImageUrl = (url) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'))
  }

  const addImageUrl = () => {
    if (newImageUrl && newImageUrl.trim()) {
      const url = newImageUrl.trim()
      if (validateImageUrl(url)) {
        setFormData(prev => ({
          ...prev,
          images: [...(Array.isArray(prev.images) ? prev.images : []), url]
        }))
        setNewImageUrl('')
      } else {
        alert('Please enter a valid URL starting with http:// or https://')
      }
    }
  }

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: (Array.isArray(prev.images) ? prev.images : []).filter((_, i) => i !== index)
    }))
  }

  const handleFileUploadComplete = (uploadResults) => {
    const newUrls = uploadResults.map(result => result.url).filter(url => url)
    if (newUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...(Array.isArray(prev.images) ? prev.images : []), ...newUrls]
      }))
    }
  }

  const handleAudioUploadComplete = (uploadResults) => {
    if (uploadResults.length > 0) {
      setFormData(prev => ({
        ...prev,
        audioLink: uploadResults[0].url
      }))
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-gray-800/20">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Add New Class</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Number (1-285)
              </label>
              <input
                type="number"
                min="1"
                max="285"
                value={formData.classNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, classNumber: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zoom Recording URLs
              </label>
              <div className="space-y-2">
                <input
                  type="url"
                  value={formData.zoomLink}
                  onChange={(e) => setFormData(prev => ({ ...prev, zoomLink: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="https://zoom.us/rec/share/... (Primary Recording)"
                />
                <input
                  type="url"
                  value={formData.zoomLink2}
                  onChange={(e) => setFormData(prev => ({ ...prev, zoomLink2: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="https://zoom.us/rec/share/... (Secondary Recording - Optional)"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                placeholder="Write your class notes here..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Audio
              </label>
              <div className="space-y-3">
                <input
                  type="url"
                  value={formData.audioLink}
                  onChange={(e) => setFormData(prev => ({ ...prev, audioLink: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="https://example.com/audio.mp3"
                />
                <div className="text-sm text-gray-500 text-center">or</div>
                <FileUpload
                  type="audio"
                  classNumber={parseInt(formData.classNumber) || 0}
                  accept="audio/*"
                  onUploadComplete={handleAudioUploadComplete}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images
              </label>
              <div className="space-y-3">
                {/* URL Input */}
                <div className="flex space-x-2">
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addImageUrl()
                      }
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="https://example.com/image.jpg"
                  />
                  <button
                    type="button"
                    onClick={addImageUrl}
                    className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <SafeIcon icon={FiPlus} />
                  </button>
                </div>

                <div className="text-sm text-gray-500 text-center">or</div>

                {/* File Upload */}
                <FileUpload
                  type="image"
                  classNumber={parseInt(formData.classNumber) || 0}
                  accept="image/*"
                  multiple={true}
                  onUploadComplete={handleFileUploadComplete}
                />

                {/* Image Preview Grid */}
                {Array.isArray(formData.images) && formData.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-20 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/200x150?text=Image+Error'
                            e.target.className += ' opacity-50'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <SafeIcon icon={FiX} />
                        </button>
                        {!validateImageUrl(image) && (
                          <div className="absolute bottom-1 left-1 px-1 py-0.5 bg-yellow-500 text-white text-xs rounded">
                            Invalid URL
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiSave} />
                <span>{loading ? 'Adding...' : 'Add Class'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AddClassModal